import { listingsModel } from "models/listings";
import type { AccountBranding } from "models/types/agency-settings";
import { NotFound404Screen } from "screens/not-found-404-screen";
import { AppLayout } from "components/app-layout";
import { FeaturePreferencesConditionalComponent } from "components/feature-preferences/feature-preferences-conditional-component";
import {
  AgencySettingsProvider,
  type AgencySettingsLoaderData,
} from "contexts/agency-settings-provider";
import { RequireAuthRole } from "contexts/auth-provider";
import { queryClient } from "lib/react-query";
import { type Route } from "lib/react-router-dom";
import { LeftNavigation } from "modules/vendor-portal/components/left-navigation";
import { VENDOR_PORTAL_PATHS } from "modules/vendor-portal/paths";
import { AgentUpdatesScreen } from "modules/vendor-portal/screens/agent-updates/agent-updates-screen";
import { DocumentsScreen } from "modules/vendor-portal/screens/documents/documents-screen";
import { EventsScreen } from "modules/vendor-portal/screens/events/events-screen";
import { FeedbackScreen } from "modules/vendor-portal/screens/feedback/feedback-screen";
import { MarketingScreen } from "modules/vendor-portal/screens/marketing/marketing-screen";
import { OffersScreen } from "modules/vendor-portal/screens/offers/offers-screen";
import { OpenHomeDetailsScreen } from "modules/vendor-portal/screens/open-home-details/open-home-details-screen";
import { OverviewScreen } from "modules/vendor-portal/screens/overview/overview-screen";
import { PerformanceScreen } from "modules/vendor-portal/screens/performance/performance-screen";
import { lockedRouteConfig } from "modules/vendor-portal/utils/locked-routes-config";
import { getRoleFromToken } from "utils/auth";
import { cachedLoader } from "utils/cache-router-loader";
import { createLockedRoute } from "utils/create-locked-route";
import { normaliseAccountBranding } from "utils/normalise-account-branding";

export const VENDOR_PORTAL_ROUTES: Route[] = [
  {
    path: VENDOR_PORTAL_PATHS.DEFAULT,
    handle: {
      title: "Vendor",
    },
    loader: cachedLoader(
      ({ params }) => `agency-settings-${params.propertyId}`,
      ({ params }): AgencySettingsLoaderData => {
        const listingData = queryClient.fetchQuery(
          listingsModel.queries.read(params.propertyId),
        );

        return {
          agencySettings: listingData.then((listing) => {
            return {
              branding: normaliseAccountBranding(
                listing?.portal?.account?.branding ?? ({} as AccountBranding),
              ),
              preferences: listing?.portal?.account?.preferences,
            };
          }),
        };
      },
    ),
    Component: () => (
      <RequireAuthRole anyOf={["vendor", "agent", "agent_manager"]}>
        <AgencySettingsProvider>
          <AppLayout
            leftNavigationMenu={<LeftNavigation />}
            showPreviewBanner={
              !!getRoleFromToken() && getRoleFromToken() !== "vendor"
            }
          />
        </AgencySettingsProvider>
      </RequireAuthRole>
    ),
    children: [
      {
        index: true,
        Component: OverviewScreen,
        handle: {
          title: "Overview",
        },
      },
      {
        ...createLockedRoute({
          path: VENDOR_PORTAL_PATHS.AGENT_UPDATES,
          Component: AgentUpdatesScreen,
          ...lockedRouteConfig[VENDOR_PORTAL_PATHS.AGENT_UPDATES],
        }),
      },
      {
        ...createLockedRoute({
          path: VENDOR_PORTAL_PATHS.DOCUMENTS,
          Component: DocumentsScreen,
          ...lockedRouteConfig[VENDOR_PORTAL_PATHS.DOCUMENTS],
        }),
      },
      {
        ...createLockedRoute({
          path: VENDOR_PORTAL_PATHS.OFFERS,
          ...lockedRouteConfig[VENDOR_PORTAL_PATHS.OFFERS],

          element: (
            <FeaturePreferencesConditionalComponent
              feature={"vendor_portal.navigation.show_offers"}
              fallback={<NotFound404Screen />}
            >
              <OffersScreen />
            </FeaturePreferencesConditionalComponent>
          ),
        }),
      },
      {
        ...createLockedRoute({
          path: VENDOR_PORTAL_PATHS.MARKETING,
          Component: MarketingScreen,
          ...lockedRouteConfig[VENDOR_PORTAL_PATHS.MARKETING],
        }),
      },
      {
        ...createLockedRoute({
          path: VENDOR_PORTAL_PATHS.EVENTS,
          Component: EventsScreen,
          ...lockedRouteConfig[VENDOR_PORTAL_PATHS.EVENTS],
        }),
      },
      {
        ...createLockedRoute({
          path: VENDOR_PORTAL_PATHS.EVENT,
          Component: OpenHomeDetailsScreen,
          ...lockedRouteConfig[VENDOR_PORTAL_PATHS.EVENT],
        }),
      },
      {
        ...createLockedRoute({
          path: VENDOR_PORTAL_PATHS.PERFORMANCE,
          Component: PerformanceScreen,
          ...lockedRouteConfig[VENDOR_PORTAL_PATHS.PERFORMANCE],
        }),
      },
      {
        ...createLockedRoute({
          path: VENDOR_PORTAL_PATHS.FEEDBACK,
          Component: FeedbackScreen,
          ...lockedRouteConfig[VENDOR_PORTAL_PATHS.FEEDBACK],
        }),
      },
    ],
  },
];
